import React, {useEffect} from "react";
import PayPalExpressButtons from "./PayPalExpressButtons";
import {DISPATCH_ACTION, FUNDING, ReactPayPalScriptOptions, usePayPalScriptReducer} from "@paypal/react-paypal-js";
import {useUnsafeCartContext} from "../../../cart/CartContextProvider/CartContextProvider";
import PayPalSingleButton from "./PayPalSingleButton";
import ErrorBoundary from "../../../utils/ErrorBoundary";
import {ButtonPropType} from "./helpers/paypalTypes";
import {isPayLaterAllowed} from "./helpers/paypalHelperFunctions";

/**
 * This component mainly checks whether there is a braintree token or not, and if so,
 * it dispatches a `resetOptions` with the braintree token and the derived client id
 * @param props properties of type `ButtonPropType`
 * @constructor
 */
const BraintreeButton: React.FC<ButtonPropType> = (props) => {
  const [{options, isResolved}, dispatch] = usePayPalScriptReducer();
  const context = useUnsafeCartContext();
  const isSummary = props.place === "purchase";

  useEffect(() => {
    if (__DEVELOPMENT__) {
      // eslint-disable-next-line no-console
      console.log("BraintreeButton props.place:", props.place);
    }

    if (context?.braintreeData) {
      // Just for error handling
      let prefix = (isSummary ? "PP" : "PPX") + " dispatch";
      try {
        let value: ReactPayPalScriptOptions;

        if (!options.dataClientToken) {
          // Initially set token to show button
          prefix += " set data client token";
          value = {
            ...options,
            dataClientToken: context.braintreeData.token,
            commit: isSummary,
            enableFunding: isPayLaterAllowed(props.place) ? FUNDING.PAYLATER : undefined,
          };
        } else if (isSummary !== options.commit) {
          // Toggle button to show "Proceed to checkout" or "Purchase Now"
          prefix += " toggle commit";
          value = {...options, commit: isSummary};
        } else if (!!options.enableFunding !== isPayLaterAllowed(props.place)) {
          // No paylater on PDP
          prefix += " toggle enableFunding";
          value = {...options, enableFunding: isPayLaterAllowed(props.place) ? FUNDING.PAYLATER : undefined};
        } else {
          return;
        }

        dispatch({type: DISPATCH_ACTION.RESET_OPTIONS, value});
      } catch (e) {
        props.handleError(e as Error, prefix);
      }
    }
  }, [context, options.dataClientToken]);

  // Did we retrieve the braintree token? if so, has the dispatch finished the `resetOptions`
  if (!context?.braintreeData || !options.dataClientToken || isSummary !== options.commit || !isResolved) {
    if (props.place === "pdp") {
      return (
        <div className="apple-pay-button-container">
          <div className="spinner-container">
            <PayPalLoadingSpinner classes="bottom-margin" />
          </div>
        </div>
      );
    }

    return <PayPalLoadingSpinner classes="bottom-margin" />;
  }

  return (
    <ErrorBoundary
      handleError={props.handleError}
      fallback={null}
      occurrence={`PayPal${isSummary ? "" : "Express"} Error boundary`}
    >
      {isSummary ? <PayPalSingleButton {...props} /> : <PayPalExpressButtons {...props} />}
    </ErrorBoundary>
  );
};

export default BraintreeButton;

/**
 * Loading spinner just for the PayPal checkout buttons
 * @constructor
 */
export const PayPalLoadingSpinner: React.FC<{classes?: string; background?: "dark" | "bright"}> = ({
  classes,
  background = "bright",
}) => {
  return (
    <div className={`paypal-loading-div${classes ? ` ${classes}` : ""}`}>
      <div className={`swiper-lazy-preloader${background === "bright" ? "" : " swiper-lazy-preloader-white"}`}>
        {/* empty div for loading spinner */}
      </div>
      &nbsp;
    </div>
  );
};

/**
 * [Styling options](https://developer.paypal.com/docs/business/checkout/reference/style-guide/#customize-the-payment-buttons) for customizing the button appearance.
 */
type PayPalLabelType = "checkout" | "paypal";
type PayPalLayoutType = "vertical" | "horizontal";
export const buttonStyleObjectExpress = {
  height: 40,
  label: "checkout" as PayPalLabelType,
  layout: "vertical" as PayPalLayoutType,
};

export const buttonStyleObjectPDP = {
  ...buttonStyleObjectExpress,
  height: 48,
};

export const buttonStyleObjectSummary = {
  ...buttonStyleObjectExpress,
  label: "paypal" as PayPalLabelType,
  layout: "horizontal" as PayPalLayoutType,
  tagline: false,
};
